import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Globe, Phone, Mail } from 'lucide-react';
import { useComponentTranslations } from '../hooks/useTranslations';
import LanguageSelector from './LanguageSelector';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { t, loading } = useComponentTranslations('header');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  if (loading) {
    return (
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div className="w-20 h-8 bg-gray-200 rounded animate-pulse"></div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-lg shadow-xl' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4 min-h-[80px] landscape:py-2 landscape:min-h-[60px]">
          {/* Logo */}
          <motion.div 
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-3 flex-shrink-0 transition-opacity duration-300 opacity-100"
          >
            <div className="flex flex-col sm:flex-row items-center space-x-0 sm:space-x-3 landscape:space-x-2">
              {/* Dreamline Logo Container */}
              <div className={`transition-all duration-300 ${
                isScrolled ? 'text-gray-800' : 'text-white'
              }`}>
                <img 
                  src={`${process.env.PUBLIC_URL}/images/MainSolidSVG.svg`}
                  alt="Dreamline - Empowering Businesses. Guiding Success."
                  className="h-16 w-16 xs:h-20 xs:w-20 sm:h-24 sm:w-24 md:h-32 md:w-32 lg:h-40 lg:w-40 xl:h-48 xl:w-48 landscape:h-20 landscape:w-20 sm:landscape:h-24 sm:landscape:w-24"
                  style={{
                    filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))',
                    transition: 'all 0.3s ease-in-out'
                  }}
                  onError={(e) => {
                    console.log('Logo failed to load:', e);
                  }}
                />
              </div>
              
              {/* Company Name and Subtitle */}
              <div className="flex flex-col min-w-0">
                <p className={`text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-bold leading-tight transition-all duration-300 landscape:text-xs sm:landscape:text-sm ${isScrolled ? 'text-teal-600' : 'text-teal-300'}`}>
                  {t('company_name', 'Dreamline Operations LLC')}
                </p>
                <p className={`text-xs xs:text-sm sm:text-base md:text-base lg:text-lg leading-tight transition-all duration-300 landscape:text-xs ${isScrolled ? 'text-gray-500' : 'text-white/70'}`}>
                  {t('tagline', 'Streamline. Strengthen. Succeed.')}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8 flex-shrink-0 landscape:space-x-4">
            {[
              { key: 'services', href: '#services' },
              { key: 'about', href: '#about' },
              { key: 'insights', href: '#insights' },
              { key: 'contact', href: '#contact' }
            ].map((item) => (
              <motion.button
                key={item.key}
                whileHover={{ scale: 1.05 }}
                onClick={() => scrollToSection(item.href.replace('#', ''))}
                className={`font-medium text-base lg:text-lg xl:text-xl transition-all duration-300 whitespace-nowrap landscape:text-sm ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-teal-600' 
                    : 'text-white hover:text-teal-300'
                }`}
              >
                {t(item.key)}
              </motion.button>
            ))}
          </nav>

          {/* Language & CTA */}
          <div className="hidden lg:flex items-center space-x-3 xl:space-x-4 flex-shrink-0">
            <div className={`hidden sm:flex items-center space-x-2 transition-colors duration-300 ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}>
              <Phone size={16} />
              <a href="tel:+18668844248" className="hover:text-teal-300 transition-colors duration-300">
                866-884-4248
              </a>
            </div>

            <LanguageSelector compact={isScrolled} isScrolled={isScrolled} />

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => scrollToSection('contact')}
              className="bg-gradient-to-r from-dream-teal to-evergreen text-white px-4 lg:px-6 xl:px-8 py-2 lg:py-3 rounded-lg font-semibold text-sm lg:text-base xl:text-lg shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap"
            >
              {t('get_started', 'Get Started')}
            </motion.button>
          </div>

          {/* Mobile Menu Button - Fixed Touch Target */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-3 rounded-lg transition-all duration-300 flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center landscape:p-2 landscape:min-w-[40px] landscape:min-h-[40px] ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
          >
            {isMenuOpen ? <X size={24} className="landscape:w-5 landscape:h-5" /> : <Menu size={24} className="landscape:w-5 landscape:h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="lg:hidden bg-white border-t shadow-lg"
        >
          <div className="px-4 py-8 space-y-6">
            {[
              { key: 'home', href: '#hero' },
              { key: 'services', href: '#services' },
              { key: 'about', href: '#about' },
              { key: 'insights', href: '#insights' },
              { key: 'contact', href: '#contact' }
            ].map((item) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.href.replace('#', ''))}
                className="block w-full text-left py-3 text-gray-700 hover:text-teal-600 font-medium text-lg"
              >
                {t(item.key)}
              </button>
            ))}
            {/* Mobile Language Selector */}
            <div className="pt-4 border-t border-gray-200">
              <LanguageSelector showText={true} />
            </div>

            {/* Mobile CTA */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => scrollToSection('contact')}
              className="w-full bg-gradient-to-r from-dream-teal to-evergreen text-white py-3 rounded-lg font-semibold text-lg mt-6"
            >
              {t('get_started', 'Get Started')}
            </motion.button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;